import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';
import {
  WEEK_SWIPE_LOCK_PX,
  isWeekSwipeIgnoreTarget,
  weekSwipeCommitFromDelta,
  type WeekSwipeCommit,
} from '../utils/dashboardWeeklyWeekSwipe';

const WEEK_SLIDE_MS = 480;
const WHEEL_GESTURE_IDLE_MS = 280;

type Args = {
  enabled: boolean;
  mondayIso: string;
  canPrev: boolean;
  canNext: boolean;
  onPrevWeek: () => void;
  onNextWeek: () => void;
};

export function useDashboardWeeklyWeekSwipe({
  enabled,
  mondayIso,
  canPrev,
  canNext,
  onPrevWeek,
  onNextWeek,
}: Args): {
  viewportRef: RefObject<HTMLDivElement | null>;
  trackRef: RefObject<HTMLDivElement | null>;
  viewportClassName: string;
  trackClassName: string;
  trackStyle: CSSProperties;
} {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [slide, setSlide] = useState<Exclude<WeekSwipeCommit, 'center'> | null>(null);
  const [instant, setInstant] = useState(false);

  const canPrevRef = useRef(canPrev);
  const canNextRef = useRef(canNext);
  const onPrevRef = useRef(onPrevWeek);
  const onNextRef = useRef(onNextWeek);
  canPrevRef.current = canPrev;
  canNextRef.current = canNext;
  onPrevRef.current = onPrevWeek;
  onNextRef.current = onNextWeek;

  const slideRef = useRef<Exclude<WeekSwipeCommit, 'center'> | null>(null);
  const suppressClickRef = useRef(false);
  const finishTimerRef = useRef(0);

  useLayoutEffect(() => {
    if (!enabled) return;
    slideRef.current = null;
    setSlide(null);
    setInstant(true);
    const id = window.requestAnimationFrame(() => setInstant(false));
    return () => window.cancelAnimationFrame(id);
  }, [enabled, mondayIso]);

  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!enabled || !viewport || !track) return undefined;

    let pointerId: number | null = null;
    let startX = 0;
    let startY = 0;
    let startedOnField = false;
    let gestureLock = false;
    let pointerHeld = false;
    let wheelIdleTimer = 0;

    const trigger = (commit: WeekSwipeCommit) => {
      if (commit === 'center' || gestureLock || slideRef.current) return false;
      gestureLock = true;
      slideRef.current = commit;
      setInstant(false);
      setSlide(commit);
      window.clearTimeout(finishTimerRef.current);
      finishTimerRef.current = window.setTimeout(() => finishSlide(commit), WEEK_SLIDE_MS + 40);
      return true;
    };

    const finishSlide = (commit: Exclude<WeekSwipeCommit, 'center'>) => {
      window.clearTimeout(finishTimerRef.current);
      if (slideRef.current !== commit) return;
      setInstant(true);
      setSlide(null);
      slideRef.current = null;
      if (commit === 'prev') onPrevRef.current();
      else onNextRef.current();
      window.requestAnimationFrame(() => setInstant(false));
      if (!pointerHeld && wheelIdleTimer === 0) gestureLock = false;
    };

    const endWheelGesture = () => {
      window.clearTimeout(wheelIdleTimer);
      wheelIdleTimer = 0;
      if (!pointerHeld && !slideRef.current) gestureLock = false;
    };

    const onTransitionEnd = (e: TransitionEvent) => {
      if (e.target !== track || e.propertyName !== 'transform') return;
      const commit = slideRef.current;
      if (commit) finishSlide(commit);
    };

    const detachPointerWindow = () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (pointerId == null || e.pointerId !== pointerId) return;
      if (gestureLock || slideRef.current) {
        if (e.cancelable) e.preventDefault();
        return;
      }
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const slop = startedOnField ? 36 : WEEK_SWIPE_LOCK_PX;
      if (Math.hypot(dx, dy) < slop) return;
      if (Math.abs(dx) <= Math.abs(dy)) {
        pointerHeld = false;
        pointerId = null;
        detachPointerWindow();
        return;
      }
      if (e.cancelable) e.preventDefault();
      suppressClickRef.current = true;
      if (startedOnField && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      try {
        viewport.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      trigger(
        weekSwipeCommitFromDelta({
          delta: dx,
          axis: 'pointer',
          canPrev: canPrevRef.current,
          canNext: canNextRef.current,
        })
      );
    };

    const onPointerUp = (e: PointerEvent) => {
      if (pointerId == null || e.pointerId !== pointerId) return;
      pointerId = null;
      pointerHeld = false;
      detachPointerWindow();
      try {
        if (viewport.hasPointerCapture(e.pointerId)) viewport.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      if (!slideRef.current) gestureLock = false;
    };
    const onPointerCancel = (e: PointerEvent) => {
      if (pointerId == null || e.pointerId !== pointerId) return;
      pointerId = null;
      pointerHeld = false;
      detachPointerWindow();
      if (!slideRef.current) gestureLock = false;
    };

    const onPointerDown = (e: PointerEvent) => {
      if (!e.isPrimary || e.button !== 0) return;
      if (e.pointerType === 'mouse' && e.detail > 1) return;
      if (slideRef.current) return;
      if (gestureLock && pointerHeld) return;
      if (gestureLock && !pointerHeld) gestureLock = false;
      pointerHeld = true;
      pointerHeld = true;
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      startedOnField = isWeekSwipeIgnoreTarget(e.target);
      window.addEventListener('pointermove', onPointerMove, { passive: false });
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerCancel);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!(gestureLock || slideRef.current) || !e.cancelable) return;
      e.preventDefault();
    };

    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      if (gestureLock || slideRef.current) {
        e.preventDefault();
        window.clearTimeout(wheelIdleTimer);
        wheelIdleTimer = window.setTimeout(endWheelGesture, WHEEL_GESTURE_IDLE_MS);
        return;
      }
      if (Math.abs(e.deltaX) < WEEK_SWIPE_LOCK_PX) return;
      e.preventDefault();
      trigger(
        weekSwipeCommitFromDelta({
          delta: e.deltaX,
          axis: 'wheel',
          canPrev: canPrevRef.current,
          canNext: canNextRef.current,
        })
      );
      window.clearTimeout(wheelIdleTimer);
      wheelIdleTimer = window.setTimeout(endWheelGesture, WHEEL_GESTURE_IDLE_MS);
    };

    const onClickCapture = (e: MouseEvent) => {
      if (!suppressClickRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      suppressClickRef.current = false;
    };

    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('click', onClickCapture, true);
    viewport.addEventListener('wheel', onWheel, { passive: false });
    viewport.addEventListener('touchmove', onTouchMove, { passive: false });
    track.addEventListener('transitionend', onTransitionEnd);

    return () => {
      window.clearTimeout(finishTimerRef.current);
      window.clearTimeout(wheelIdleTimer);
      detachPointerWindow();
      viewport.removeEventListener('pointerdown', onPointerDown);
      viewport.removeEventListener('click', onClickCapture, true);
      viewport.removeEventListener('wheel', onWheel);
      viewport.removeEventListener('touchmove', onTouchMove);
      track.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [enabled]);

  return {
    viewportRef,
    trackRef,
    viewportClassName: 'cp-dash-weekly-week-viewport',
    trackClassName:
      'cp-dash-weekly-week-track' +
      (instant ? ' is-week-instant' : '') +
      (slide === 'prev' ? ' is-week-prev' : '') +
      (slide === 'next' ? ' is-week-next' : ''),
    trackStyle: {},
  };
}
